// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { RotateCcw, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActiveFiltersDisplay } from "@/components/active-filters-display";
import { FilterPopover, type FilterValue } from "@/components/filter-popover";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterSelect } from "@/components/ui/filter-select";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { SortIcon } from "@/components/ui/sort-icon";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type Campaign,
  type CampaignsQueryParams,
  useCampaigns,
} from "@/hooks/api/use-campaigns";
import { useDebounce } from "@/hooks/use-debounce";
import { buildFilterGroupFromColumnState } from "@/lib/filters";
import { cn } from "@/lib/utils";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";

// Default sort configuration
const DEFAULT_SORT_COLUMN = "start_date";
const DEFAULT_SORT_DIRECTION: SortDirection = "desc";

interface BenchmarkDataTableProps {
  columns: UnifiedTableColumn<unknown>[];
  onCountChange?: (count: number) => void;
  onFiltersChange?: (filters: Record<string, unknown>) => void;
  tableFilters?: Record<string, unknown> | null;
  currentCampaignId?: string | null;
  excludeCurrentCampaign?: boolean;
  onExcludeCurrentCampaignChange?: (exclude: boolean) => void;
  currentCampaignName?: string;
  // For static mode
  selectionMode?: boolean;
  selectedCampaignIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  // Callback for when we determine if current campaign matches filters
  onCurrentCampaignMatchesChange?: (matches: boolean) => void;
}

/**
 * A completely self-contained benchmark table component.
 * Manages its own state and API calls without dependencies on other table components.
 */
export function BenchmarkDataTable({
  columns,
  onCountChange,
  onFiltersChange,
  tableFilters = {},
  currentCampaignId = null,
  excludeCurrentCampaign = true,
  onExcludeCurrentCampaignChange,
  currentCampaignName = "current campaign",
  selectionMode = false,
  selectedCampaignIds = [],
  onSelectionChange,
  onCurrentCampaignMatchesChange,
}: BenchmarkDataTableProps) {
  // State for internal filters
  const [filters, setFilters] = useState<Record<string, unknown>>(() => ({
    sortColumn: DEFAULT_SORT_COLUMN,
    sortDirection: DEFAULT_SORT_DIRECTION,
    ...(tableFilters || {}),
  }));

  // State for UI elements
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: SortDirection;
  }>({
    key: (filters.sortColumn as string) || DEFAULT_SORT_COLUMN,
    direction:
      (filters.sortDirection as SortDirection) || DEFAULT_SORT_DIRECTION,
  });
  const [searchInputValue, setSearchInputValue] = useState<string>(
    (filters.search as string) || "",
  );
  // Apply debouncing to avoid too many queries while typing
  const debouncedSearchQuery = useDebounce(searchInputValue, 300);

  // Sync filters with parent
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [filters, onFiltersChange]);

  // Filter update functions
  const updateFilter = useCallback((key: string, value: FilterValue) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilter = useCallback((key: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({
      sortColumn: DEFAULT_SORT_COLUMN,
      sortDirection: DEFAULT_SORT_DIRECTION,
    });
    setSortConfig({
      key: DEFAULT_SORT_COLUMN,
      direction: DEFAULT_SORT_DIRECTION,
    });
    setSearchInputValue("");
  }, []);

  // Reset function for table reset button
  const handleResetTable = useCallback(() => {
    clearAllFilters();
    // Reset selection if in selection mode
    if (selectionMode && onSelectionChange) {
      onSelectionChange([]);
    }
  }, [clearAllFilters, selectionMode, onSelectionChange]);

  // Initialize filters from props if they change
  // Use a ref to track previous tableFilters to prevent infinite loops
  const prevTableFiltersRef = useRef<
    Record<string, unknown> | null | undefined
  >(null);

  useEffect(() => {
    if (!tableFilters) return;

    // Skip effect if tableFilters hasn't changed (by reference)
    if (tableFilters === prevTableFiltersRef.current) return;
    prevTableFiltersRef.current = tableFilters;

    // Compare filters without sort parameters for a more accurate change detection
    const compareFiltersWithoutSort = (
      filtersA: Record<string, unknown>,
      filtersB: Record<string, unknown>,
    ) => {
      const aWithoutSort = { ...filtersA };
      const bWithoutSort = { ...filtersB };

      // Remove sort parameters from both objects
      delete aWithoutSort.sortColumn;
      delete aWithoutSort.sortDirection;
      delete bWithoutSort.sortColumn;
      delete bWithoutSort.sortDirection;

      return JSON.stringify(aWithoutSort) === JSON.stringify(bWithoutSort);
    };

    // Only update if the non-sort filters have actually changed
    if (!compareFiltersWithoutSort(tableFilters, filters)) {
      // Update filters while preserving sort state
      setFilters((prevFilters) => ({
        sortColumn: prevFilters.sortColumn || DEFAULT_SORT_COLUMN,
        sortDirection: prevFilters.sortDirection || DEFAULT_SORT_DIRECTION,
        ...(tableFilters || {}),
      }));

      // Update search input if needed
      if (
        tableFilters.search !== undefined &&
        tableFilters.search !== searchInputValue
      ) {
        setSearchInputValue(tableFilters.search as string);
      } else if (tableFilters.search === undefined && searchInputValue !== "") {
        setSearchInputValue("");
      }
    }
  }, [tableFilters, filters, searchInputValue]);

  // Clear search when switching between modes
  useEffect(() => {
    // If in dynamic mode, clear the search
    if (!selectionMode) {
      setSearchInputValue("");
      // Remove search from filters if present
      if (filters.search) {
        clearFilter("search");
      }
    }
  }, [selectionMode, clearFilter, filters.search, filters]);

  // Check if there are active filters
  const hasFilters = useMemo(
    () =>
      Object.keys(filters).filter(
        (key) => key !== "sortColumn" && key !== "sortDirection",
      ).length > 0,
    [filters],
  );

  // Update filter when debounced search query changes
  useEffect(() => {
    if (debouncedSearchQuery) {
      updateFilter("search", debouncedSearchQuery);
    } else if (filters.search) {
      clearFilter("search");
    }
  }, [debouncedSearchQuery, clearFilter, updateFilter, filters.search]);

  // Generate query params for API
  const queryParams = useMemo(() => {
    // Create a base params object without including filters directly
    // Use a very high limit to load all campaigns at once (10,000 should be more than enough)
    const params: CampaignsQueryParams = {
      limit: 10000,
      search: debouncedSearchQuery,
    };

    // Only add exclude_id if both the ID is provided and the exclude flag is true
    if (currentCampaignId && excludeCurrentCampaign) {
      params.exclude_id = currentCampaignId;
    }

    // Map internal sort parameters to API expected parameters
    if (filters.sortColumn) {
      params.sort_by = filters.sortColumn as string;
    }

    if (filters.sortDirection) {
      params.sort_direction = filters.sortDirection as SortDirection;
    }

    // -----------------------------------------------------------------
    // Structured filters JSON
    // -----------------------------------------------------------------

    const validKeys = new Set(columns.map((c) => c.key.toString()));
    const group = buildFilterGroupFromColumnState(filters, validKeys);
    if (group) {
      params.filters = JSON.stringify(group);
    }

    return params;
  }, [
    filters,
    debouncedSearchQuery,
    currentCampaignId,
    excludeCurrentCampaign,
    columns,
  ]);

  // Fetch campaigns from API - use regular query instead of infinite query
  const { data, isLoading, isError, error } = useCampaigns(queryParams);

  // Create a memoized version of campaigns to prevent dependency changes
  const campaigns = useMemo(() => data?.results || [], [data]);

  // Update parent with campaign count when data changes
  useEffect(() => {
    if (onCountChange && data?.total !== undefined) {
      // Simply pass the total from the API response
      onCountChange(data.total);
    }
  }, [data, onCountChange]);

  // Also notify parent when exclude toggle changes without waiting for refetch
  useEffect(() => {
    // This will ensure the parent is notified when the exclude toggle changes
    if (onCountChange && data?.total !== undefined) {
      onCountChange(data.total);
    }
  }, [onCountChange, data]);

  // Handle sorting - wrapped in useCallback to prevent recreation on each render
  const handleSort = useCallback(
    (key: string) => {
      const column = columns.find((col) => col.key === key);
      if (!column?.sortable) return;

      let direction: SortDirection = column?.defaultSortDirection || "asc";

      if (sortConfig.key === key) {
        // Toggle between asc and desc
        direction = sortConfig.direction === "asc" ? "desc" : "asc";
      }

      setSortConfig({ key, direction });
      updateFilter("sortColumn", key);
      updateFilter("sortDirection", direction);
    },
    [columns, sortConfig, updateFilter],
  );

  // Handle search input - now just updates the input value,
  // the debounced effect will handle the filter update
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchInputValue("");
  };

  // Render sort icon - wrapped in useCallback to prevent recreation on each render
  const renderSortIcon = useCallback(
    (column: { key: string; sortable?: boolean }) => {
      if (!column.sortable) return null;

      return (
        <SortIcon
          isActive={sortConfig.key === column.key}
          direction={
            sortConfig.key === column.key ? sortConfig.direction : null
          }
        />
      );
    },
    [sortConfig.key, sortConfig.direction],
  );

  // The campaigns declaration was moved up above the onCountChange effect

  // Campaign selection handlers (for static mode)
  const handleCampaignSelection = useCallback(
    (id: string, checked: boolean) => {
      if (!onSelectionChange) return;

      if (checked) {
        // Add to selection if not already there
        if (!selectedCampaignIds.includes(id)) {
          onSelectionChange([...selectedCampaignIds, id]);
        }
      } else {
        // Remove from selection
        onSelectionChange(selectedCampaignIds.filter((cid) => cid !== id));
      }
    },
    [selectedCampaignIds, onSelectionChange],
  );

  // Handle select all
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!onSelectionChange) return;

      if (checked) {
        // Select all visible campaigns that aren't already selected
        const visibleCampaignIds = campaigns.map((c) => c.id);
        const newSelectedIds = [...selectedCampaignIds];

        visibleCampaignIds.forEach((id) => {
          if (!newSelectedIds.includes(id)) {
            newSelectedIds.push(id);
          }
        });

        onSelectionChange(newSelectedIds);
      } else {
        // Deselect all visible campaigns
        const visibleCampaignIds = campaigns.map((c) => c.id);
        const newSelectedIds = selectedCampaignIds.filter(
          (id) => !visibleCampaignIds.includes(id),
        );

        onSelectionChange(newSelectedIds);
      }
    },
    [campaigns, selectedCampaignIds, onSelectionChange],
  );

  // Determine if all visible campaigns are selected
  const isAllSelected = useMemo(() => {
    if (campaigns.length === 0) return false;
    return campaigns.every((campaign) =>
      selectedCampaignIds.includes(campaign.id),
    );
  }, [campaigns, selectedCampaignIds]);

  // Memoize loading state to prevent unnecessary re-renders
  const loadingState = useMemo(
    () => (
      <>
        <TableHeader>
          <TableRow className="bg-muted/50">
            {selectionMode && (
              <TableHead className="w-[50px]">
                <div className="flex items-center justify-center">
                  <Checkbox
                    data-testid="benchmark-select-all-checkbox"
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all"
                    disabled={campaigns.length === 0}
                  />
                </div>
              </TableHead>
            )}
            {columns
              .filter((column) => !column.exportOnly)
              .map((column) => (
                <TableHead
                  key={column.key.toString()}
                  className={cn(
                    column.key === "actions" ? "text-right" : "",
                    column.width,
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs flex items-center p-2 rounded-md">
                      {column.title}
                    </span>
                  </div>
                </TableHead>
              ))}
          </TableRow>
        </TableHeader>
        <TableBody className="bg-card">
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              {selectionMode && (
                <TableCell className="w-[50px]">
                  <div className="flex justify-center">
                    <Skeleton className="h-4 w-4" />
                  </div>
                </TableCell>
              )}
              {columns
                .filter((column) => !column.exportOnly)
                .map((column) => (
                  <TableCell
                    key={`${i}-${column.key.toString()}`}
                    className={cn(
                      column.width,
                      column.key === sortConfig.key && "bg-muted/40",
                    )}
                  >
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
            </TableRow>
          ))}
        </TableBody>
      </>
    ),
    [
      selectionMode,
      columns,
      isAllSelected,
      handleSelectAll,
      campaigns.length,
      sortConfig.key,
    ],
  );

  // Check for current campaign presence and notify parent when it changes
  useEffect(() => {
    if (selectionMode || !currentCampaignId) return;
    const currentCampaignInResults = campaigns.some(
      (campaign) => campaign.id === currentCampaignId,
    );
    // Notify parent component if callback is provided
    if (onCurrentCampaignMatchesChange) {
      onCurrentCampaignMatchesChange(currentCampaignInResults);
    }
  }, [
    campaigns,
    currentCampaignId,
    selectionMode,
    onCurrentCampaignMatchesChange,
  ]);

  // Memoize table content to prevent unnecessary re-renders
  const tableContent = useMemo(() => {
    return (
      <>
        <TableHeader>
          <TableRow className="bg-muted/50">
            {selectionMode && (
              <TableHead className="w-[50px]">
                <div className="flex items-center justify-center">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all"
                    disabled={campaigns.length === 0}
                  />
                </div>
              </TableHead>
            )}
            {columns
              .filter((column) => !column.exportOnly)
              .map((column) => (
                <TableHead
                  key={column.key.toString()}
                  className={cn(
                    column.key === "actions" ? "text-right" : "",
                    column.width,
                  )}
                >
                  <div className="flex items-center justify-between">
                    {column.sortable ? (
                      <div className="flex items-center w-full">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent flex items-center font-medium group w-full justify-start hover:bg-muted/50"
                          onClick={() => handleSort(column.key)}
                        >
                          {column.title}
                        </Button>
                        <span
                          className="flex-shrink-0 flex justify-center cursor-pointer"
                          onClick={() => handleSort(column.key)}
                        >
                          {renderSortIcon(column)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs flex items-center p-2 rounded-md hover:bg-muted/50">
                        {column.title}
                      </span>
                    )}

                    {column.filterable &&
                      (column.filterType === "select" ? (
                        <FilterSelect
                          column={column as UnifiedTableColumn}
                          currentFilter={filters[column.key]}
                          onFilterChange={updateFilter}
                          onClearFilter={clearFilter}
                        />
                      ) : (
                        <FilterPopover
                          column={column as UnifiedTableColumn}
                          currentFilter={filters[column.key] as FilterValue}
                          onFilterChange={updateFilter}
                          onClearFilter={clearFilter}
                        />
                      ))}
                  </div>
                </TableHead>
              ))}
          </TableRow>
        </TableHeader>
        <TableBody className="bg-card">
          {isError ? (
            <TableRow>
              <TableCell
                colSpan={selectionMode ? columns.length + 1 : columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                Error loading campaigns: {error.message}
              </TableCell>
            </TableRow>
          ) : campaigns.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={selectionMode ? columns.length + 1 : columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                No campaigns found.
              </TableCell>
            </TableRow>
          ) : (
            campaigns.map((campaign, index) => (
              <TableRow
                key={campaign.id}
                className={cn(
                  // Only apply styling to current campaign in dynamic mode
                  currentCampaignId &&
                    campaign.id === currentCampaignId &&
                    !selectionMode
                    ? excludeCurrentCampaign
                      ? "line-through text-muted-foreground"
                      : "bg-muted/30" // Highlight the current campaign when not excluded
                    : "",
                )}
              >
                {selectionMode && (
                  <TableCell className="w-[50px]">
                    <div className="flex justify-center">
                      <Checkbox
                        data-testid={`benchmark-select-campaign-checkbox-${index}`}
                        checked={selectedCampaignIds.includes(campaign.id)}
                        onCheckedChange={(checked) =>
                          handleCampaignSelection(campaign.id, !!checked)
                        }
                        aria-label={`Select ${campaign.name}`}
                      />
                    </div>
                  </TableCell>
                )}
                {columns
                  .filter((column) => !column.exportOnly)
                  .map((column) => (
                    <TableCell
                      key={`${campaign.id}-${column.key.toString()}`}
                      className={cn(
                        column.key === "actions" ? "text-right" : "",
                        column.width,
                        column.key === sortConfig.key && "bg-muted/40",
                      )}
                    >
                      <div
                        title={String(
                          campaign[column.key as keyof Campaign] ?? "",
                        )}
                      >
                        {column.render
                          ? column.render(campaign)
                          : String(
                              campaign[column.key as keyof Campaign] ?? "",
                            )}
                      </div>
                    </TableCell>
                  ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </>
    );
  }, [
    selectionMode,
    columns,
    campaigns,
    selectedCampaignIds,
    isAllSelected,
    handleSelectAll,
    isError,
    error,
    currentCampaignId,
    excludeCurrentCampaign,
    handleCampaignSelection,
    sortConfig.key,
    filters,
    clearFilter,
    updateFilter,
    handleSort,
    renderSortIcon,
  ]);

  return (
    <Card className="h-full relative">
      {/* Top row with search, filters, exclude toggle, and results count */}
      <div className="p-4">
        {selectionMode ? (
          /* Static mode layout - search input on top, active filters below */
          <>
            <div className="flex items-center justify-between gap-4 mb-2">
              {/* Search Input - Only show in Static mode */}
              <div className="relative">
                <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Filter by campaign name..."
                  className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
                  onChange={handleSearchChange}
                  value={searchInputValue}
                />
                {searchInputValue && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                    onClick={clearSearch}
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground/80" />
                  </Button>
                )}
              </div>
              <InfoTooltip
                trigger={
                  <button
                    className="ml-2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Wildcard search info"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <InfoCircledIcon className="h-3.5 w-3.5" />
                  </button>
                }
                side="right"
                sideOffset={5}
              >
                <div className="text-sm space-y-2">
                  <p className="font-medium">Supports wildcards</p>
                  <ul className="space-y-1">
                    <li>
                      <code className="px-1 py-0.5 bg-muted rounded text-xs">
                        _
                      </code>{" "}
                      matches a single character
                    </li>
                    <li>
                      <code className="px-1 py-0.5 bg-muted rounded text-xs">
                        %
                      </code>{" "}
                      matches zero or more characters
                    </li>
                  </ul>
                </div>
              </InfoTooltip>

              {/* Right side with reset button and exclude toggle */}
              <div className="flex items-center gap-2 ml-auto">
                {/* Reset Table Button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleResetTable}
                      className="h-8 w-8"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Reset table to default state</p>
                  </TooltipContent>
                </Tooltip>

                {/* Exclude current campaign toggle */}
                {currentCampaignId && onExcludeCurrentCampaignChange && (
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="exclude-current"
                      checked={excludeCurrentCampaign}
                      onCheckedChange={onExcludeCurrentCampaignChange}
                    />
                    <Label
                      htmlFor="exclude-current"
                      className="text-sm text-muted-foreground whitespace-nowrap"
                    >
                      Exclude this campaign ({currentCampaignName})
                    </Label>
                  </div>
                )}
              </div>
            </div>

            {/* Active Filters Display */}
            {hasFilters && (
              <div className="flex items-center mt-2">
                <ActiveFiltersDisplay
                  filters={Object.fromEntries(
                    Object.entries(filters).filter(
                      ([key]) =>
                        key !== "sortColumn" && key !== "sortDirection",
                    ),
                  )}
                  columns={columns as UnifiedTableColumn[]}
                  clearFilter={clearFilter}
                  clearAllFilters={clearAllFilters}
                  clearAllText="Clear all filters"
                  filterType="table"
                />
              </div>
            )}
          </>
        ) : (
          /* Dynamic mode - active filters and exclude toggle in same row */
          <div className="flex items-center mb-2">
            {/* Active Filters Display - Left side */}
            <div className="flex-grow overflow-hidden">
              {hasFilters ? (
                <ActiveFiltersDisplay
                  filters={Object.fromEntries(
                    Object.entries(filters).filter(
                      ([key]) =>
                        key !== "sortColumn" && key !== "sortDirection",
                    ),
                  )}
                  columns={columns as UnifiedTableColumn[]}
                  clearFilter={clearFilter}
                  clearAllFilters={clearAllFilters}
                  clearAllText="Clear all filters"
                  filterType="table"
                />
              ) : null}
            </div>

            {/* Right side with reset button and exclude toggle */}
            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
              {/* Reset Table Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleResetTable}
                    className="h-8 w-8"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset table to default state</p>
                </TooltipContent>
              </Tooltip>

              {/* Exclude toggle */}
              {currentCampaignId && onExcludeCurrentCampaignChange && (
                <div className="flex items-center">
                  <Switch
                    id="exclude-current"
                    checked={excludeCurrentCampaign}
                    onCheckedChange={onExcludeCurrentCampaignChange}
                  />
                  <Label
                    htmlFor="exclude-current"
                    className="text-sm text-muted-foreground whitespace-nowrap ml-2"
                  >
                    Exclude this campaign ({currentCampaignName})
                  </Label>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Remove flex-grow, min-h-0, add fixed max-height */}
      <div className="rounded-md border overflow-y-auto max-h-[40vh]">
        <Table>{isLoading ? loadingState : tableContent}</Table>
      </div>
    </Card>
  );
}
